import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  type ListProps,
  Paper,
  type Theme,
  Typography,
} from '@mui/material';
import { Translate, type Translation } from '@shared/translation';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

interface MenuContextValue {
  onClose: () => void;
}

const MenuContext = createContext<MenuContextValue>({
  onClose: () => {},
});

interface MenuProps extends ListProps {
  onClose?: () => void;
  minWidth?: string | number;
}

// TODO: This was copied from SAM for the table extraction. If another shared
// surface needs it, move it out to a generic shared UI Menu component.
export const Menu = ({
  onClose = () => {},
  minWidth = '250px',
  children,
  ...listProps
}: MenuProps) => (
  <Paper elevation={8}>
    <MenuContext.Provider value={{ onClose }}>
      <List {...listProps} sx={{ minWidth }}>
        {children}
      </List>
    </MenuContext.Provider>
  </Paper>
);

const getColor = (color: 'primary' | 'error' | undefined, theme: Theme) =>
  color === 'error' ? theme.palette.error.main : undefined;

interface MenuItemProps {
  icon?: ReactNode;
  iconAfter?: ReactNode;
  onClick: () => boolean | void;
  disabled?: boolean;
  color?: 'primary' | 'error';
  selected?: boolean;
}

interface WithLabel extends MenuItemProps {
  label: Translation;
  children?: never;
}

interface WithChildren extends MenuItemProps {
  label?: never;
  children: ReactNode;
}

export const MenuItem = ({
  children,
  disabled,
  onClick,
  label,
  icon,
  iconAfter,
  color = 'primary',
  selected,
}: WithLabel | WithChildren) => {
  const { onClose } = useContext(MenuContext);

  return (
    <ListItemButton
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        const keepOpen = onClick();
        if (!keepOpen) {
          onClose();
        }
      }}
      sx={(theme) => ({
        color: getColor(color, theme),
        backgroundColor: selected
          ? theme.palette.grey[theme.palette.mode === 'light' ? 200 : 700]
          : 'unset',
      })}
      disableRipple
    >
      {icon && (
        <ListItemIcon
          sx={{
            minWidth: '40px',
            color: (theme) => getColor(color, theme),
          }}
        >
          {icon}
        </ListItemIcon>
      )}
      <ListItemText>
        <Typography variant="body2">
          {label ? <Translate text={label} /> : children}
        </Typography>
      </ListItemText>
      {iconAfter && (
        <ListItemIcon sx={{ minWidth: 'unset' }}>{iconAfter}</ListItemIcon>
      )}
    </ListItemButton>
  );
};
